import type { RailwayPurpose } from "../../railway-query/types";
import { initialRailwayFares, matchesRailwayFare, savedRailwayFares } from "../service/itineraryRailwayViewService";
import type { ItineraryRailwayFare } from "../service/itineraryRailwayViewService";
import { ItineraryRailwayQueryPanel } from "./ItineraryRailwayQueryPanel";
import { ItineraryRailwayTransferPanel } from "./ItineraryRailwayTransferPanel";
import { useItineraryRailwayTransfers, type RailwayTransferFetcher } from "../runtime/useItineraryRailwayTransfers";
import { createRailwayTransferPlan, railwayTransferUnavailableReason, savedRailwayTransferSelections } from "../service/itineraryRailwayTransferQueryService";
import { Alert, Button, DatePicker, Drawer, Modal, Radio, Select, Spin } from "@arco-design/web-react";
import {
  ArrowRight,
  Bus,
  CalendarDays,
  Clock3,
  Footprints,
  MapPin,
  Plane,
  ReceiptText,
  RotateCw,
  Route,
  Train,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { isHttpClientError } from "../../../lib/http/errors";
import {
  applyDrivingMeasurement,
  applyManualSegmentEdit,
  applyTransportSelection,
  applyWalkingMeasurement,
  clearTransportFactsForEndpointChange,
  formatDistanceKm,
  formatTransportTimeRange,
  hasPersistedTransportFacts,
  localTransportEndpoint,
  localTransportErrorMessage,
  normalizeTransportSelectionDuration,
  queryLocalTransport,
  transportEndpointDraft,
} from "../service";
import type { LocalTransportAutomaticSelection } from "../service/itineraryLocalTransportService";
import {
  selection,
  alignTransportSelectionDate,
  addIsoDays,
  addIsoMonths,
  selectedSeat,
  canSelectTransportOption,
  isPurchasableSeat,
  transportSelectionKey,
  localDateIso,
  normalizeTransportDate,
  railwayEndpointChanges,
  railwayReferenceNotice,
} from "../service/itineraryTimetableService";
import type {
  DrivingRequest,
  DrivingResult,
  FlightQuery,
  FlightSearchResult,
  ItineraryNode,
  ItinerarySegment,
  PointCandidate,
  PointCandidateResult,
  PointCandidateQuery,
  RailwayQuery,
  RailwaySearchResult,
  RailwayTransferPlan,
  RailwayTransferContext,
  TransportEndpointChange,
  TransportOption,
  TransportSelection,
  TransportMode,
  WalkingRequest,
  WalkingResult,
} from "../types";
import { ItineraryDialogCloseButton } from "./ItineraryDialogCloseButton";
import { ItineraryRouteFields } from "./ItineraryRouteFields";
import {
  initializeRouteDraft,
  prepareRouteDraftForSubmit,
  routeConstraintWarning,
  switchRouteTransportMode,
  transportModeLabels,
  withEstimatedBusRouteTimes,
} from "./itineraryRouteDraft";

type Props = {
  /** 嵌入点位弹窗时只展示火车查询与选择，不允许保存空方案。 */
  embeddedRailway?: boolean;
  railwaySelectionNote?: string;
  railwaySelectionMode?: "compare";
  railwayFooterContainer?: HTMLElement | null;
  confirmDisabled?: boolean;
  visible: boolean;
  segment: ItinerarySegment | null;
  fromNode: ItineraryNode | null;
  toNode: ItineraryNode | null;
  railwayTransferContext?: RailwayTransferContext | null;
  travelDate?: string | null;
  departureDayDate?: string | null;
  arrivalDayDate?: string | null;
  onCancel: () => void;
  onConfirm: (segment: ItinerarySegment, endpointChanges?: TransportEndpointChange[], railwayTransfer?: RailwayTransferPlan) => void;
  afterClose?: () => void;
  onDraftStateChange?: (dirty: boolean) => void;
  getRailwayOptions: (query: RailwayQuery, signal?: AbortSignal) => Promise<RailwaySearchResult>;
  getRailwayTransfers?: RailwayTransferFetcher;
  getFlightOptions?: (query: FlightQuery, signal?: AbortSignal) => Promise<FlightSearchResult>;
  getDrivingRoute?: (request: DrivingRequest, signal?: AbortSignal) => Promise<DrivingResult>;
  getWalkingRoute?: (request: WalkingRequest, signal?: AbortSignal) => Promise<WalkingResult>;
  listPointCandidates?: (query: PointCandidateQuery, signal?: AbortSignal) => Promise<PointCandidateResult>;
  professionalOptions?: Array<{ segmentId: string; name: string; color?: string }>;
  onProfessionalChange?: (segmentId: string) => void;
};

/** 仅火车入口不提供其他交通服务，防止意外调用。 */
async function unavailableTransport(): Promise<never> {
  throw new Error("当前入口只支持火车查询");
}

const TRANSPORT_ENDPOINT_KEYWORD_MAX_LENGTH = 100;
const transportChoices = [
  { mode: "walk", label: "步行", icon: Footprints },
  { mode: "bus", label: "大巴", icon: Bus },
  { mode: "rail", label: "火车", icon: Train },
  { mode: "air", label: "飞机", icon: Plane },
] as const;
const workspaceDialogAttributes = { role: "dialog", "aria-label": "编辑路线" } as const;

const localTransportDistanceFormatter = new Intl.NumberFormat("zh-CN", {
  maximumFractionDigits: 3,
  useGrouping: false,
});

/** 将自动比较过程解释给用户，避免只看到最终方式而误以为系统反复切换失败。 */
function localTransportAutomaticNotice(selection?: LocalTransportAutomaticSelection): string {
  if (!selection) return "";
  const busDistance = `${localTransportDistanceFormatter.format(selection.busDistanceKm)} 公里`;
  const walkingDistance = `${localTransportDistanceFormatter.format(selection.walkingDistanceKm)} 公里`;
  if (selection.kind === "bus_to_walk") {
    return `大巴先按地点名称测算为 ${busDistance}，小于 800 米；步行按起终点名称取得高德坐标（命中坐标缓存时直接复用，名称定位失败时才使用该端有效后备），再测算得到 ${walkingDistance}，也小于 800 米，已自动切换为步行。`;
  }
  return `大巴先按地点名称测算为 ${busDistance}，小于 800 米；步行按起终点名称取得高德坐标（命中坐标缓存时直接复用，名称定位失败时才使用该端有效后备）追加测算，得到 ${walkingDistance}，达到或超过 800 米，最终保留大巴。若两次距离与实际不符，请核对起终点名称和定位；手工请求若把起点和终点写成同一坐标，返回 1 米只代表这次请求的两点相同。`;
}

/** 交通工作区共用查询、选价与确认逻辑，可作为抽屉或嵌入式火车选择器。 */
export function ItineraryTransportWorkspace({
  embeddedRailway = false,
  railwaySelectionNote,
  railwaySelectionMode,
  railwayFooterContainer,
  confirmDisabled = false,
  visible,
  segment,
  fromNode,
  toNode,
  railwayTransferContext,
  travelDate,
  departureDayDate,
  arrivalDayDate,
  onCancel,
  onConfirm,
  afterClose,
  onDraftStateChange: onDraftStateChangeProp,
  getRailwayOptions,
  getRailwayTransfers,
  getFlightOptions = unavailableTransport,
  getDrivingRoute = unavailableTransport,
  getWalkingRoute,
  listPointCandidates,
  professionalOptions,
  onProfessionalChange,
}: Props) {
  const [draftSegment, setDraftSegment] = useState<ItinerarySegment | null>(segment);
  const [railwayFares, setRailwayFares] = useState<ItineraryRailwayFare[]>([]);
  const compareRailway = railwaySelectionMode === "compare";
  const [railwayPurpose, setRailwayPurpose] = useState<RailwayPurpose>("ADULT");
  const [railwayMode, setRailwayMode] = useState<"direct" | "transfer">("direct");
  const railwayManualRef = useRef(false);
  const pendingDateQueryRef = useRef(false);
  const [railwayQueryRevision, setRailwayQueryRevision] = useState(0);
  const [railwayQueryScope, setRailwayQueryScope] = useState<"station" | "region">("station");
  const [acceptedRailwayEndpointChanges, setAcceptedRailwayEndpointChanges] = useState<TransportEndpointChange[]>([]);
  const [originNodeDraft, setOriginNodeDraft] = useState<ItineraryNode | null>(fromNode);
  const [destinationNodeDraft, setDestinationNodeDraft] = useState<ItineraryNode | null>(toNode);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [date, setDate] = useState("");
  const [earliestDeparture, setEarliestDeparture] = useState("");
  const [latestDeparture, setLatestDeparture] = useState("");
  const [options, setOptions] = useState<TransportOption[]>([]);
  const [departureSort, setDepartureSort] = useState<"asc" | "desc">("asc");
  const [railwayMeta, setRailwayMeta] = useState<RailwaySearchResult | null>(null);
  const [railwayTimeWindowFallback, setRailwayTimeWindowFallback] = useState(false);
  const [railwaySavedOptionsFallback, setRailwaySavedOptionsFallback] = useState(false);
  const [flightMeta, setFlightMeta] = useState<FlightSearchResult | null>(null);
  const [selectedKey, setSelectedKey] = useState("");
  const [backupKeys, setBackupKeys] = useState<string[]>([]);
  const [selectedSeatClasses, setSelectedSeatClasses] = useState<Record<string, string>>({});
  const [driving, setDriving] = useState<DrivingResult | null>(null);
  const [walking, setWalking] = useState<WalkingResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dateNotice, setDateNotice] = useState("");
  const [localTransportNotice, setLocalTransportNotice] = useState("");
  const [resultKey, setResultKey] = useState("");
  const [hasQueried, setHasQueried] = useState(false);
  const [pendingProfessionalSegmentId, setPendingProfessionalSegmentId] = useState<string | null>(null);
  const draftDirtyRef = useRef(false);
  const professionalSelectRef = useRef<HTMLSelectElement | null>(null);
  const generationRef = useRef(0);
  const requestAbortRef = useRef<AbortController | null>(null);
  const contextRef = useRef("");
  const autoQueryTriggeredRef = useRef(false);
  const autoQuerySnapshotKeyRef = useRef("");
  const queryCurrentRef = useRef<(preserveSavedRailwayFacts?: boolean) => void>(() => undefined);
  const persistedSelectionInitializedRef = useRef(false);
  const walkingRoute = getWalkingRoute ?? (async () => { throw new Error("步行测算暂不可用"); });
  const onDraftStateChange = (dirty: boolean) => {
    draftDirtyRef.current = dirty;
    onDraftStateChangeProp?.(dirty);
  };

  const sourceSegmentKey = segment
    ? [segment.id, segment.routeType, segment.transportMode, segment.fromNodeId, segment.toNodeId].join(":")
    : "none";
  const segmentFactsKey = segment
    ? [segment.sourceType, segment.routeCode, segment.departureAt, segment.arrivalAt,
      segment.distanceKm, segment.durationMinutes, segment.sourcePayload?.selected?.optionId,
      segment.sourcePayload?.toolResultSnapshot?.queriedAt].join(":")
    : "none";
  const activeSegment = draftSegment ?? segment;
  const selectedFromNode = originNodeDraft ?? fromNode;
  const selectedToNode = destinationNodeDraft ?? toNode;
  const segmentKey = activeSegment
    ? [activeSegment.id, activeSegment.routeType, activeSegment.transportMode, activeSegment.fromNodeId, activeSegment.toNodeId].join(":")
    : "none";
  const originStationIds = selectedFromNode?.nodeType === "railway_station" && selectedFromNode.pointSpuId
    ? [selectedFromNode.pointSpuId]
    : [];
  const destinationStationIds = selectedToNode?.nodeType === "railway_station" && selectedToNode.pointSpuId
    ? [selectedToNode.pointSpuId]
    : [];
  const exactEndpointsValid = activeSegment?.transportMode === "rail"
    ? Boolean(originStationIds.length && destinationStationIds.length)
    : activeSegment?.transportMode === "air"
      ? Boolean(
          selectedFromNode?.nodeType === "airport"
          && selectedFromNode.pointSpuId?.trim()
          && selectedToNode?.nodeType === "airport"
          && selectedToNode.pointSpuId?.trim()
        )
      : Boolean(selectedFromNode && selectedToNode);
  const endpointQueryKey = activeSegment?.transportMode === "rail"
    ? `${originStationIds.join(",")}|${destinationStationIds.join(",")}|${railwayQueryScope}`
    : `${origin.trim()}|${destination.trim()}`;
  const currentQueryKey = `${segmentKey}|${endpointQueryKey}|${date}|${earliestDeparture}|${latestDeparture}${railwayPurpose === "ADULT" ? "" : "|0X00"}`;
  contextRef.current = `${visible ? "open" : "closed"}|${currentQueryKey}|${railwayMode}`;
  const transferRuntime = useItineraryRailwayTransfers({
    active: visible && activeSegment?.transportMode === "rail" && railwayMode === "transfer",
    contextKey: `${segmentKey}|${selectedFromNode?.title}|${selectedToNode?.title}`,
    query: { fromStationIds: originStationIds, toStationIds: destinationStationIds, date, queryScope: railwayQueryScope },
    fetcher: getRailwayTransfers,
    initialSelections: savedRailwayTransferSelections(
      selectedFromNode?.pointSpuId === railwayTransferContext?.fromNode.pointSpuId
        && selectedToNode?.pointSpuId === railwayTransferContext?.toNode.pointSpuId
        ? railwayTransferContext?.plan : undefined,
    ),
  });

  /** 火车工作区打开时，历史或当天行程默认查询明天；日期控件仍允许选择今天。 */
  const initialQueryDate = (mode: TransportMode | undefined, value: string | null | undefined) => {
    const normalized = normalizeTransportDate(mode, value ?? "");
    if (mode === "rail" && (!normalized || normalized <= localDateIso())) return addIsoDays(localDateIso(), 1);
    return normalized;
  };

  const clearResults = (preserveRailwayFares = false) => {
    transferRuntime.cancel(preserveRailwayFares);
    requestAbortRef.current?.abort();
    requestAbortRef.current = null;
    generationRef.current += 1;
    if (!preserveRailwayFares) setRailwayFares([]);
    setOptions([]);
    setRailwayMeta(null);
    setRailwayTimeWindowFallback(false);
    setRailwaySavedOptionsFallback(false);
    setFlightMeta(null);
    setSelectedKey("");
    setBackupKeys([]);
    setSelectedSeatClasses({});
    setDriving(null);
    setWalking(null);
    setError("");
    setResultKey("");
    setLocalTransportNotice("");
    setLoading(false);
    persistedSelectionInitializedRef.current = true;
  };

  const updateRouteDraft = (next: ItinerarySegment) => {
    const modeChanged = activeSegment?.transportMode !== next.transportMode;
    if (modeChanged) {
      setAcceptedRailwayEndpointChanges([]);
      clearResults();
      setHasQueried(false);
      persistedSelectionInitializedRef.current = false;
      const normalizedDate = normalizeTransportDate(next.transportMode, date);
      setDate(normalizedDate);
      setDateNotice(transportDateNotice(next.transportMode, date));
      const hasTimetableQuery = next.transportMode === "air";
      setEarliestDeparture(hasTimetableQuery ? "09:00" : "");
      setLatestDeparture(hasTimetableQuery ? "18:00" : "");
    }
    setDraftSegment(next);
    onDraftStateChange?.(true);
  };

  const changeTransportMode = (transportMode: TransportMode) => {
    if (!activeSegment || activeSegment.transportMode === transportMode) return;
    const routeType = transportMode === "walk"
      ? "local_transfer"
      : transportMode === "bus"
        ? "charter_bus"
        : "main_transport";
    updateRouteDraft(switchRouteTransportMode(activeSegment, routeType, transportMode));
  };

  const changeTransportChoice = (choice: TransportMode | "rail_region") => {
    if (!activeSegment) return;
    const mode = choice === "rail_region" ? "rail" : choice;
    const scope = choice === "rail_region" ? "region" : "station";
    if (mode !== "rail" && railwayMode === "transfer" && railwayTransferContext) {
      setRailwayMode("direct");
      setOriginNodeDraft(fromNode); setDestinationNodeDraft(toNode);
      setOrigin(fromNode?.title ?? ""); setDestination(toNode?.title ?? "");
    }
    if (mode === "rail" && (activeSegment.transportMode !== "rail" || scope !== railwayQueryScope)) {
      clearResults();
      setHasQueried(false);
      setRailwayQueryScope(scope);
      setAcceptedRailwayEndpointChanges([]);
      if (activeSegment.transportMode === "rail") {
        setDraftSegment(clearTransportFactsForEndpointChange(activeSegment));
        onDraftStateChange?.(true);
      }
    }
    changeTransportMode(mode);
  };

  const changeEndpoint = (kind: "origin" | "destination", node: ItineraryNode) => {
    if (!activeSegment) return;
    setAcceptedRailwayEndpointChanges([]);
    if (activeSegment.transportMode === "rail") setRailwayQueryScope("station");
    clearResults();
    setHasQueried(false);
    setDraftSegment(clearTransportFactsForEndpointChange(activeSegment));
    if (kind === "origin") {
      setOriginNodeDraft(node);
      setOrigin(node.title.trim());
    } else {
      setDestinationNodeDraft(node);
      setDestination(node.title.trim());
    }
    onDraftStateChange?.(true);
  };

  useEffect(() => {
    railwayManualRef.current = false;
    setRailwayMode(railwayTransferContext ? "transfer" : "direct");
    setRailwayFares(savedRailwayFares(segment));
    setRailwayPurpose("ADULT");
    pendingDateQueryRef.current = false;
    draftDirtyRef.current = false;
    setPendingProfessionalSegmentId(null);
    requestAbortRef.current?.abort();
    requestAbortRef.current = null;
    generationRef.current += 1;
    autoQueryTriggeredRef.current = false;
    persistedSelectionInitializedRef.current = false;
    const initialFromNode = railwayTransferContext?.fromNode ?? fromNode;
    const initialToNode = railwayTransferContext?.toNode ?? toNode;
    const snapshotOrigin = initialFromNode?.title.trim() ?? "";
    const snapshotDestination = initialToNode?.title.trim() ?? "";
    const snapshotDate = initialQueryDate(segment?.transportMode, travelDate);
    const snapshotEarliest = segment?.transportMode === "air" ? "09:00" : "";
    const snapshotLatest = segment?.transportMode === "air" ? "18:00" : "";
    const snapshotScope = initialFromNode?.nodeType === "railway_station"
      && initialToNode?.nodeType === "railway_station"
      && initialFromNode.railwayRegionName && initialToNode.railwayRegionName ? "region" : "station";
    setRailwayQueryScope(snapshotScope);
    setDepartureSort("asc");
    setAcceptedRailwayEndpointChanges([]);
    const initialOriginStationIds = initialFromNode?.nodeType === "railway_station" && initialFromNode.pointSpuId
      ? [initialFromNode.pointSpuId]
      : [];
    const initialDestinationStationIds = initialToNode?.nodeType === "railway_station" && initialToNode.pointSpuId
      ? [initialToNode.pointSpuId]
      : [];
    const initialEndpointsValid = segment?.transportMode === "rail"
      ? Boolean(initialOriginStationIds.length && initialDestinationStationIds.length)
      : segment?.transportMode === "air"
        ? Boolean(
            initialFromNode?.nodeType === "airport"
            && initialFromNode.pointSpuId?.trim()
            && initialToNode?.nodeType === "airport"
            && initialToNode.pointSpuId?.trim()
          )
        : Boolean(initialFromNode && initialToNode);
    const canAutoQuery = Boolean(
      visible
      && segment
      && (segment.transportMode === "rail" || !hasPersistedTransportFacts(segment))
      && (
        ["rail", "air"].includes(segment.transportMode)
        || (segment.transportMode === "bus" && segment.sourceType === "pending")
      )
      && initialEndpointsValid
      && snapshotOrigin
      && snapshotDestination
      && (segment.transportMode === "bus" || snapshotDate),
    );
    const endpointSnapshot = segment?.transportMode === "rail"
      ? `${initialOriginStationIds.join(",")}|${initialDestinationStationIds.join(",")}|${snapshotScope}`
      : `${snapshotOrigin}|${snapshotDestination}`;
    autoQuerySnapshotKeyRef.current = canAutoQuery
      ? `${sourceSegmentKey}|${endpointSnapshot}|${snapshotDate}|${snapshotEarliest}|${snapshotLatest}`
      : "";
    setDraftSegment(segment ? initializeRouteDraft(segment) : null);
    setOriginNodeDraft(initialFromNode ? structuredClone(initialFromNode) : null);
    setDestinationNodeDraft(initialToNode ? structuredClone(initialToNode) : null);
    setOrigin(snapshotOrigin);
    setDestination(snapshotDestination);
    setDate(snapshotDate);
    const initialNotice = segment?.transportMode === "rail" && travelDate && snapshotDate !== normalizeTransportDate("rail", travelDate)
      ? `原行程日期 ${travelDate} 不在火车可查询范围，查询日期已调整为 ${snapshotDate}，点击查询后使用。`
      : transportDateNotice(segment?.transportMode, travelDate ?? "");
    setDateNotice(initialNotice);
    setEarliestDeparture(snapshotEarliest);
    setLatestDeparture(snapshotLatest);
    setOptions([]);
    setRailwayMeta(null);
    setRailwayTimeWindowFallback(false);
    setRailwaySavedOptionsFallback(false);
    setFlightMeta(null);
    setSelectedKey("");
    setBackupKeys([]);
    setSelectedSeatClasses({});
    setDriving(null);
    setWalking(null);
    setError("");
    setLocalTransportNotice("");
    setResultKey("");
    setHasQueried(false);
    setLoading(false);
  }, [
    fromNode?.id,
    fromNode?.nodeType,
    fromNode?.pointSpuId,
    fromNode?.title,
    fromNode?.railwayRegionName,
    sourceSegmentKey,
    toNode?.id,
    toNode?.nodeType,
    toNode?.pointSpuId,
    toNode?.title,
    toNode?.railwayRegionName,
    travelDate,
    visible,
  ]);

  useEffect(() => () => {
    requestAbortRef.current?.abort();
    requestAbortRef.current = null;
    generationRef.current += 1;
  }, []);

  const run = async (action: (generation: number, requestKey: string, signal: AbortSignal) => Promise<void>) => {
    requestAbortRef.current?.abort();
    const controller = new AbortController();
    requestAbortRef.current = controller;
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    const requestKey = contextRef.current;
    setOptions([]);
    setRailwayMeta(null);
    setRailwayTimeWindowFallback(false);
    setRailwaySavedOptionsFallback(false);
    setFlightMeta(null);
    setDriving(null);
    setWalking(null);
    setLoading(true);
    setHasQueried(true);
    setError("");
    setLocalTransportNotice("");
    setResultKey("");
    setSelectedKey("");
    setBackupKeys([]);
    setSelectedSeatClasses({});
    try {
      await action(generation, requestKey, controller.signal);
    } catch (reason) {
      if (controller.signal.aborted) return;
      if (generation === generationRef.current && requestKey === contextRef.current) {
        const savedSnapshot = activeSegment?.sourcePayload?.toolResultSnapshot;
        const savedFallback = activeSegment?.transportMode === "rail"
          && isHttpClientError(reason)
          && reason.code === "ITINERARY_TRANSPORT_EMPTY"
          ? railwayFallbackOptions(
              activeSegment,
              railwaySnapshotQueryKey(originStationIds, destinationStationIds, date),
              date,
              selectedFromNode?.id,
              selectedToNode?.id,
              selectedFromNode?.title ?? origin,
              selectedToNode?.title ?? destination,
              railwayQueryScope,
            )
          : null;
        if (savedFallback?.options.length) {
          const filtered = filterByDepartureTime(savedFallback.options, earliestDeparture, latestDeparture);
          const displayOptions = savedFallback.source === "saved-route"
            ? savedFallback.options
            : filtered.length ? filtered : savedFallback.options;
          setRailwayMeta({
            requestedDate: date,
            queryDate: savedFallback.source === "snapshot" ? savedSnapshot?.referenceDate ?? date : date,
            isReference: savedFallback.source === "snapshot" && Boolean(savedSnapshot?.isReference),
            referenceDate: savedFallback.source === "snapshot" ? savedSnapshot?.referenceDate ?? null : null,
            options: savedFallback.options,
          });
          setRailwayTimeWindowFallback(savedFallback.source === "snapshot" && filtered.length === 0);
          setRailwaySavedOptionsFallback(
            savedFallback.source === "saved-route" || filtered.length > 0,
          );
          setOptions(displayOptions);
          setRailwayFares((current) => current.length || railwayManualRef.current ? current : savedRailwayFares(activeSegment));
          applyResultSelections(displayOptions);
          setResultKey(currentQueryKey);
          return;
        }
        const emptyRailwayMessage = activeSegment?.transportMode === "rail"
          ? railwayNoResultsMessage(
              selectedFromNode?.title ?? origin,
              selectedToNode?.title ?? destination,
              date,
              earliestDeparture,
              latestDeparture,
              false,
            )
          : undefined;
        setError(activeSegment?.transportMode === "bus" || activeSegment?.transportMode === "walk"
          ? localTransportErrorMessage(reason, {
              origin: { name: selectedFromNode?.title ?? origin },
              destination: { name: selectedToNode?.title ?? destination },
            })
          : transportErrorMessage(reason, emptyRailwayMessage));
      }
    } finally {
      if (generation === generationRef.current && requestKey === contextRef.current) {
        setLoading(false);
      }
      if (requestAbortRef.current === controller) requestAbortRef.current = null;
    }
  };

  const rejectInvalidTimeWindow = () => {
    if (!earliestDeparture || !latestDeparture || earliestDeparture <= latestDeparture) return false;
    clearResults();
    setError("最早出发时间不能晚于最晚出发时间，仅支持同日时间窗。");
    return true;
  };

  const rejectPastTravelDate = () => {
    if (!date || date >= localDateIso()) return false;
    clearResults();
    setHasQueried(true);
    setError("交通查询日期不能早于今天，请先调整行程日期。");
    return true;
  };

  const applyResultSelections = (availableOptions: TransportOption[]) => {
    if (!activeSegment) return;
    const restored = persistedSelectionInitializedRef.current
      ? reconcileTransportSelections(availableOptions, { selectedKey, backupKeys, seatClasses: selectedSeatClasses })
      : restorePersistedSelections(activeSegment, availableOptions);
    const next = withDefaultPrimarySelection(availableOptions, restored);
    persistedSelectionInitializedRef.current = true;
    setSelectedKey(next.selectedKey);
    setBackupKeys(next.backupKeys);
    setSelectedSeatClasses(next.seatClasses);
  };

  const queryRailway = (preserveSavedFacts = false) => {
    if (!activeSegment || rejectPastTravelDate() || rejectInvalidTimeWindow()) {
      return Promise.resolve();
    }
    return run(async (generation, requestKey, signal) => {
      const result = await getRailwayOptions({
        fromStationIds: originStationIds,
        toStationIds: destinationStationIds,
        date,
        details: true,
        queryScope: railwayQueryScope,
        ...(railwayPurpose !== "ADULT" ? { purpose: railwayPurpose } : {}),
      }, signal);
      if (generation !== generationRef.current || requestKey !== contextRef.current) return;
      const savedFallback = result.options.length === 0 && activeSegment
        ? railwayFallbackOptions(
            activeSegment,
            railwaySnapshotQueryKey(originStationIds, destinationStationIds, date),
            date,
            selectedFromNode?.id,
            selectedToNode?.id,
            selectedFromNode?.title ?? origin,
            selectedToNode?.title ?? destination,
            railwayQueryScope,
          )
        : null;
      const dailyOptions = result.options.length ? result.options : savedFallback?.options ?? [];
      const filtered = dailyOptions;
      const displayOptions = savedFallback?.source === "saved-route"
        ? savedFallback.options
        : filtered.length ? filtered : dailyOptions;
      const savedSnapshot = activeSegment?.sourcePayload?.toolResultSnapshot;
      setRailwayMeta({ ...result, options: dailyOptions,
        ...(savedFallback ? {
          requestedDate: date,
          queryDate: savedFallback.source === "snapshot" ? savedSnapshot?.referenceDate ?? date : date,
          isReference: savedFallback.source === "snapshot" && Boolean(savedSnapshot?.isReference),
          referenceDate: savedFallback.source === "snapshot" ? savedSnapshot?.referenceDate ?? null : null,
          regionQuery: railwayQueryScope === "region",
          originStation: selectedFromNode?.pointSpuId ? { refId: selectedFromNode.pointSpuId, name: selectedFromNode.title } : null,
          destinationStation: selectedToNode?.pointSpuId ? { refId: selectedToNode.pointSpuId, name: selectedToNode.title } : null,
        } : {}),
      });
      setRailwayTimeWindowFallback(
        filtered.length === 0 && dailyOptions.length > 0 && savedFallback?.source !== "saved-route",
      );
      setRailwaySavedOptionsFallback(
        Boolean(savedFallback && (savedFallback.source === "saved-route" || filtered.length > 0)),
      );
      setOptions(displayOptions);
      const keepSavedFacts = preserveSavedFacts && hasPersistedTransportFacts(activeSegment);
      setRailwayFares((current) => current.length || railwayManualRef.current || keepSavedFacts
        ? current : initialRailwayFares(displayOptions, activeSegment, result.queryDate, railwayPurpose, result));
      if (!keepSavedFacts) applyResultSelections(displayOptions);
      setResultKey(currentQueryKey);
    });
  };

  const queryFlight = () => {
    if (rejectPastTravelDate() || rejectInvalidTimeWindow()) {
      return Promise.resolve();
    }
    return run(async (generation, requestKey, signal) => {
      const result = await getFlightOptions({ origin, destination, departureDate: date }, signal);
      if (generation !== generationRef.current || requestKey !== contextRef.current) return;
      const filtered = filterByDepartureTime(result.options, earliestDeparture, latestDeparture);
      setFlightMeta({ ...result, options: filtered });
      setOptions(filtered);
      applyResultSelections(filtered);
      setResultKey(currentQueryKey);
    });
  };

  const queryDistance = (mode: "bus" | "walk") => run(async (generation, requestKey, signal) => {
    if (!activeSegment || !selectedFromNode || !selectedToNode) throw new Error("路线端点不完整");
    const measurement = await queryLocalTransport({
      origin: localTransportEndpoint(selectedFromNode, origin),
      destination: localTransportEndpoint(selectedToNode, destination),
    }, { getDrivingRoute, getWalkingRoute: walkingRoute }, signal, mode);
    if (generation !== generationRef.current || requestKey !== contextRef.current) return;
    const nextSegment = measurement.mode === mode ? activeSegment : switchRouteTransportMode(
      activeSegment, measurement.mode === "walk" ? "local_transfer" : "charter_bus", measurement.mode,
    );
    const nextSegmentKey = [nextSegment.id, nextSegment.routeType, nextSegment.transportMode, nextSegment.fromNodeId, nextSegment.toNodeId].join(":");
    setDraftSegment((current) => measurement.mode === mode ? current : switchRouteTransportMode(
      current ?? activeSegment, nextSegment.routeType, nextSegment.transportMode,
    ));
    setDriving(measurement.mode === "bus" ? measurement.result : null);
    setWalking(measurement.mode === "walk" ? measurement.result : null);
    setLocalTransportNotice(localTransportAutomaticNotice(measurement.automaticSelection));
    setResultKey(`${nextSegmentKey}|${endpointQueryKey}|${date}|${earliestDeparture}|${latestDeparture}`);
    setLoading(false);
    onDraftStateChange?.(true);
  });
  const queryDriving = () => queryDistance("bus");
  const queryWalking = () => queryDistance("walk");

  queryCurrentRef.current = (preserveSavedRailwayFacts = false) => {
    if (activeSegment?.transportMode === "rail") {
      if (railwayMode === "transfer") {
        if (exactEndpointsValid && date && date >= localDateIso()) void transferRuntime.runQuery();
      }
      else void queryRailway(preserveSavedRailwayFacts);
    }
    if (activeSegment?.transportMode === "air") void queryFlight();
    if (activeSegment?.transportMode === "bus") void queryDriving();
    if (activeSegment?.transportMode === "walk") void queryWalking();
  };

  useEffect(() => {
    if (
      !visible
      || railwayMode === "transfer"
      || !activeSegment
      || (activeSegment.transportMode !== "rail" && hasPersistedTransportFacts(activeSegment))
      || (
        activeSegment.transportMode !== "rail"
        && activeSegment.transportMode !== "air"
        && !(activeSegment.transportMode === "bus" && activeSegment.sourceType === "pending")
      )
      || !exactEndpointsValid
      || (activeSegment.transportMode !== "bus" && !date)
      || currentQueryKey !== autoQuerySnapshotKeyRef.current
      || autoQueryTriggeredRef.current
    ) return;
    autoQueryTriggeredRef.current = true;
    queryCurrentRef.current(true);
  }, [
    activeSegment?.transportMode,
    activeSegment?.sourceType,
    activeSegment?.routeCode,
    activeSegment?.departureAt,
    activeSegment?.arrivalAt,
    activeSegment?.distanceKm,
    activeSegment?.durationMinutes,
    activeSegment?.sourcePayload?.selected?.optionId,
    date,
    destination,
    origin,
    sourceSegmentKey,
    segmentFactsKey,
    visible,
  ]);

  const endpointChanges = (railway?: RailwaySearchResult | null, selected?: TransportSelection | null): TransportEndpointChange[] => {
    const resolved = activeSegment?.transportMode === "rail"
      && (railway || selected?.fromStationId !== undefined || selected?.toStationId !== undefined) && selectedFromNode && selectedToNode
      ? railwayEndpointChanges(selectedFromNode, selectedToNode, railway, selected) : acceptedRailwayEndpointChanges;
    return [
    [fromNode, selectedFromNode],
    [toNode, selectedToNode],
  ].flatMap(([previous, draft]) => {
    if (!previous || !draft || previous.id !== draft.id) return [];
    const previousDraft = transportEndpointDraft(previous);
    const nextDraft = resolved.find((item) => item.nodeId === draft.id)?.draft ?? transportEndpointDraft(draft);
    return sameTransportEndpointDraft(previousDraft, nextDraft)
      ? []
      : [{ nodeId: previous.id, previous: previousDraft, draft: nextDraft }];
    });
  };

  const finishConfirm = (nextSegment: ItinerarySegment, railway?: RailwaySearchResult | null) => {
    try {
      const changes = endpointChanges(railway, nextSegment.sourcePayload?.selected);
      onConfirm(nextSegment, changes.length ? changes : undefined);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "车次实际车站无法确认，请重新查询");
    }
  };

  useEffect(() => {
    if (!pendingDateQueryRef.current || !visible) return;
    pendingDateQueryRef.current = false;
    queryCurrentRef.current();
  }, [date, railwayQueryRevision, visible]);

  if (!activeSegment || !segment) return null;

  const isBus = activeSegment.transportMode === "bus";
  const isRail = activeSegment.transportMode === "rail";
  const isTransfer = isRail && railwayMode === "transfer";
  const isWalk = activeSegment.transportMode === "walk";
  const supportsTimetableQuery = isRail || activeSegment.transportMode === "air";
  const supportsDistanceQuery = isBus || isWalk;
  const sortedOptions = [...options].sort((left, right) => {
    const leftTime = Date.parse(left.departureAt ?? "");
    const rightTime = Date.parse(right.departureAt ?? "");
    if (!Number.isFinite(leftTime)) return Number.isFinite(rightTime) ? 1 : 0;
    if (!Number.isFinite(rightTime)) return -1;
    return (leftTime - rightTime) * (departureSort === "asc" ? 1 : -1);
  });
  const supportsQuery = supportsTimetableQuery || supportsDistanceQuery;
  const today = localDateIso();
  const maxQueryDate = isRail ? addIsoDays(today, 14) : addIsoMonths(today, 2);
  const pastTravelDate = supportsTimetableQuery && Boolean(date && date < today);
  const invalidTimeWindow = supportsTimetableQuery
    && Boolean(earliestDeparture && latestDeparture && earliestDeparture > latestDeparture);
  const localValidationError = pastTravelDate
    ? "交通查询日期不能早于今天，请先调整行程日期。"
    : invalidTimeWindow
      ? "最早出发时间不能晚于最晚出发时间，仅支持同日时间窗。"
      : "";
  const displayedError = localValidationError || error;
  const validationErrorId = "itinerary-transport-validation-error";
  const modeLabel = isRail && railwayQueryScope === "region" ? "大区域火车" : transportModeLabels[activeSegment.transportMode];
  const queryModeLabel = activeSegment.transportMode === "air" ? "航班" : modeLabel;
  const ModeIcon = isWalk ? Footprints : isBus ? Bus : isRail ? Train : Plane;
  const routeWarning = routeConstraintWarning(activeSegment, selectedFromNode, selectedToNode);
  const primaryRailwayFare = railwayFares[0];
  const selectedRailwayMeta = primaryRailwayFare?.queryResult ?? (primaryRailwayFare && railwayMeta && primaryRailwayFare.date === railwayMeta.queryDate && matchesRailwayFare(primaryRailwayFare, railwayMeta.options) ? railwayMeta : null);
  const selectedOption = options.find((option) => transportSelectionKey(option) === selectedKey);
  const persistedRouteCode = activeSegment.sourcePayload?.selected?.routeCode?.trim()
    || activeSegment.routeCode?.trim()
    || "当前路线";
  const selectedPreview = isTransfer ? null : isRail
    ? railwayFares[0] ? normalizeTransportSelectionDuration(alignTransportSelectionDate(railwayFares[0].selection, travelDate)) : null
    : selectedOption
    ? normalizeTransportSelectionDuration(alignTransportSelectionDate(
        selection(selectedOption, selectedSeat(selectedOption, selectedSeatClasses)),
        travelDate,
      ))
    : null;
  const routeDepartureDayDate = departureDayDate ?? travelDate;
  const routeArrivalDayDate = arrivalDayDate ?? departureDayDate ?? travelDate;
  const routeDraft = selectedPreview
    ? {
        ...activeSegment,
        routeCode: selectedPreview.routeCode ?? null,
        departureAt: selectedPreview.departureAt ?? null,
        arrivalAt: selectedPreview.arrivalAt ?? null,
        durationMinutes: selectedPreview.durationMinutes ?? null,
        distanceKm: selectedPreview.distanceKm ?? null,
      }
    : driving
      ? withEstimatedBusRouteTimes({
          ...activeSegment,
          distanceKm: String(driving.distanceKm),
          durationMinutes: driving.businessDurationMinutes,
        }, routeDepartureDayDate, routeArrivalDayDate)
      : walking
        ? {
            ...activeSegment,
            distanceKm: String(walking.distanceKm),
            durationMinutes: walking.businessDurationMinutes,
          }
      : activeSegment;
  const rememberRailwayEndpoints = () => {
    if (!selectedFromNode || !selectedToNode || !primaryRailwayFare) return;
    try {
      setAcceptedRailwayEndpointChanges(railwayEndpointChanges(selectedFromNode, selectedToNode, selectedRailwayMeta, primaryRailwayFare.selection));
    } catch {
      // 无法解析的车站不回填；退出方案后仍允许用户手工维护当前路线。
      setAcceptedRailwayEndpointChanges([]);
    }
  };
  const updateDisplayedRouteDraft = (next: ItinerarySegment) => {
    const manualRailwayFacts = isRail && ["routeCode", "departureAt", "arrivalAt", "durationMinutes"].some((key) => next[key as keyof ItinerarySegment] !== routeDraft[key as keyof ItinerarySegment]);
    if (manualRailwayFacts) {
      railwayManualRef.current = true;
      requestAbortRef.current?.abort(); requestAbortRef.current = null;
      generationRef.current += 1;
      setLoading(false); setRailwayFares([]); setSelectedKey("");
      persistedSelectionInitializedRef.current = true; autoQueryTriggeredRef.current = true;
    }
    if (selectedPreview) {
      const previewFactsChanged = (
        (next.routeCode ?? null) !== (routeDraft.routeCode ?? null)
        || (next.departureAt ?? null) !== (routeDraft.departureAt ?? null)
        || (next.arrivalAt ?? null) !== (routeDraft.arrivalAt ?? null)
        || (next.durationMinutes ?? null) !== (routeDraft.durationMinutes ?? null)
        || (next.distanceKm ?? null) !== (routeDraft.distanceKm ?? null)
      );
      if (previewFactsChanged) {
        rememberRailwayEndpoints();
        setSelectedKey("");
        setDraftSegment(next);
      } else {
        setDraftSegment({
          ...next,
          routeCode: activeSegment.routeCode,
          departureAt: activeSegment.departureAt,
          arrivalAt: activeSegment.arrivalAt,
          durationMinutes: activeSegment.durationMinutes,
          distanceKm: activeSegment.distanceKm,
        });
      }
      onDraftStateChange?.(true);
      return;
    }
    if (driving || walking) {
      const routeTimesChanged = (
        (next.departureAt ?? null) !== (routeDraft.departureAt ?? null)
        || (next.arrivalAt ?? null) !== (routeDraft.arrivalAt ?? null)
      );
      const measurementFactsChanged = (
        (next.distanceKm ?? null) !== (routeDraft.distanceKm ?? null)
        || (!routeTimesChanged
          && (next.durationMinutes ?? null) !== (routeDraft.durationMinutes ?? null))
      );
      if (measurementFactsChanged) {
        setDriving(null);
        setWalking(null);
        setResultKey("");
        setDraftSegment(next);
      } else {
        setDraftSegment({
          ...next,
          distanceKm: activeSegment.distanceKm,
          durationMinutes: activeSegment.durationMinutes,
        });
      }
      onDraftStateChange?.(true);
      return;
    }
    updateRouteDraft(next);
  };
  const changeDate = (nextDate: string) => {
    const normalizedDate = normalizeTransportDate(activeSegment?.transportMode, nextDate);
    const nextNotice = transportDateNotice(activeSegment?.transportMode, nextDate);
    if (!normalizedDate || normalizedDate === date) {
      if (nextNotice !== dateNotice) setDateNotice(nextNotice);
      return;
    }
    clearResults(activeSegment.transportMode === "rail");
    setDate(normalizedDate);
    setDateNotice(nextNotice);
    onDraftStateChange?.(true);
  };
  const cancelDrawer = () => {
    if (pendingProfessionalSegmentId) return;
    if (compareRailway) { onCancel(); return; }
    requestAbortRef.current?.abort();
    requestAbortRef.current = null;
    generationRef.current += 1;
    transferRuntime.cancel();
    onCancel();
  };
  const saveRoute = () => {
    if (routeWarning || confirmDisabled || (embeddedRailway && !isTransfer && !railwayFares.length)) return;
    if (isTransfer) {
      if (!transferRuntime.selections.length || !fromNode || !toNode) return;
      try {
        const plans = (compareRailway ? transferRuntime.selections.slice(0, 1) : transferRuntime.selections).map((chosen) => createRailwayTransferPlan(chosen.option, chosen.result, chosen.seatClasses, {
          travelDate, queryKey: transferRuntime.queryKey, queriedAt: chosen.queriedAt, queryScope: railwayQueryScope,
        }));
        const plan = { ...plans[0], backups: plans.slice(1) };
        const first = plan.legs[0]; const last = plan.legs[plan.legs.length - 1];
        const changes = [
          { node: railwayTransferContext?.fromNode ?? fromNode, refId: first.fromStationId!, name: first.fromName! },
          { node: railwayTransferContext?.toNode ?? toNode, refId: last.toStationId!, name: last.toName! },
        ].flatMap(({ node, refId, name }) => {
          if (node.nodeType === "railway_station" && node.pointSpuId === refId) return [];
          const previous = transportEndpointDraft(node);
          const draft = { ...previous, nodeType: "railway_station" as const, pointSpuId: refId, title: name,
            stayAreaId: null, organizationSiteId: null, description: null, imageUrl: null };
          return sameTransportEndpointDraft(previous, draft) ? [] : [{ nodeId: node.id, previous, draft }];
        });
        onConfirm({ ...clearTransportFactsForEndpointChange(activeSegment), routeType: "main_transport", transportMode: "rail" },
          changes.length ? changes : undefined, plan);
      } catch (reason) { setError(reason instanceof Error ? reason.message : "中转方案无法应用，请重新查询"); }
      return;
    }
    if (supportsDistanceQuery && resultKey === currentQueryKey && (driving || walking)) {
      const queryKey = `${origin.trim()}-${destination.trim()}`;
      finishConfirm(isBus
        ? applyDrivingMeasurement(routeDraft, driving as DrivingResult, new Date().toISOString(), queryKey)
        : applyWalkingMeasurement(routeDraft, walking as WalkingResult, new Date().toISOString(), queryKey));
      return;
    }
    if (isRail && railwayFares.length) {
      const submittedFares = compareRailway ? railwayFares.slice(0, 1) : railwayFares;
      const selections = submittedFares.map((fare) => alignTransportSelectionDate(fare.selection, travelDate));
      const previousSnapshot = activeSegment.sourcePayload?.toolResultSnapshot;
      const selectedQueryDate = selectedRailwayMeta?.requestedDate ?? primaryRailwayFare.date;
      const selectedOptions = selectedRailwayMeta
        ? selectedRailwayMeta.options.map((option) => selection(option, selectedSeat(option, selectedSeatClasses)))
        : previousSnapshot?.options ?? [];
      finishConfirm(applyTransportSelection(activeSegment, selections[0], selections.slice(1), {
        provider: "py12306", queriedAt: primaryRailwayFare.queriedAt || previousSnapshot?.queriedAt || new Date().toISOString(),
        queryKey: !selectedRailwayMeta && previousSnapshot?.queryKey ? previousSnapshot.queryKey : railwaySnapshotQueryKey(selections[0].fromStationId ? [selections[0].fromStationId] : originStationIds, selections[0].toStationId ? [selections[0].toStationId] : destinationStationIds, selectedQueryDate),
        railwayQueryScope, options: [...railwayFares.map((fare) => fare.selection), ...selectedOptions.filter((option) => !railwayFares.some((fare) => fare.selection.optionId === option.optionId || fare.selection.optionId.startsWith(`${option.optionId}~`)))],
        isReference: Boolean(selectedRailwayMeta?.isReference ?? previousSnapshot?.isReference), referenceDate: selectedRailwayMeta ? selectedRailwayMeta.referenceDate ?? null : previousSnapshot?.referenceDate ?? null,
        warnings: [...(selectedRailwayMeta?.isReference ? [railwayReferenceNotice(selectedRailwayMeta)] : previousSnapshot?.warnings ?? []), ...(travelDate && selectedQueryDate !== travelDate ? [`查询日 ${selectedQueryDate} 仅用于参考，已按行程日 ${travelDate} 写入班次时刻`] : [])],
      }), selectedRailwayMeta);
      return;
    }
    if (!isRail && supportsTimetableQuery && resultKey === currentQueryKey && selectedOption) {
      const selected = alignTransportSelectionDate(
        selection(selectedOption, selectedSeat(selectedOption, selectedSeatClasses)),
        travelDate,
      );
      const backups = options
        .filter((item) => backupKeys.includes(transportSelectionKey(item)))
        .map((item) => alignTransportSelectionDate(
          selection(item, selectedSeat(item, selectedSeatClasses)),
          travelDate,
        ));
      const referenceMeta = isRail ? railwayMeta : flightMeta;
      const referenceWarning = referenceMeta?.isReference
        ? [isRail && railwayMeta
          ? railwayReferenceNotice(railwayMeta)
          : flightMeta?.referenceReason === "query_failed"
          ? `原航班查询日期 ${flightMeta.requestedDate ?? date} 未返回可用航班，已自动改查明天 ${flightMeta.referenceDate ?? flightMeta.queryDate ?? date} 的航班，仅供参考`
          : `请求日 ${referenceMeta.requestedDate ?? date} 超出可查询范围，已改用 ${referenceMeta.referenceDate ?? referenceMeta.queryDate ?? date} 的数据，仅供参考`]
        : [];
      const adjustedDateWarning = travelDate && date !== travelDate
        ? [`查询日 ${date} 仅用于参考，已按行程日 ${travelDate} 写入班次时刻`]
        : [];
      finishConfirm(applyTransportSelection(activeSegment, selected, backups, {
        provider: selectedOption.provider,
        queriedAt: new Date().toISOString(),
        queryKey: activeSegment.transportMode === "rail"
          ? railwaySnapshotQueryKey(
              railwayMeta?.originStation ? [railwayMeta.originStation.refId] : originStationIds,
              railwayMeta?.destinationStation ? [railwayMeta.destinationStation.refId] : destinationStationIds, date,
            )
          : `${origin}-${destination}-${date}`,
        railwayQueryScope: isRail ? railwayQueryScope : null,
        options: options.map((item) => selection(item, selectedSeat(item, selectedSeatClasses))),
        isReference: Boolean(referenceMeta?.isReference),
        referenceDate: referenceMeta?.referenceDate ?? null,
        warnings: [...referenceWarning, ...adjustedDateWarning],
      }), isRail ? railwayMeta : null);
      return;
    }
    const prepared = prepareRouteDraftForSubmit(routeDraft);
    const manual = applyManualSegmentEdit(segment, prepared, { preserveEditedTransportFacts: true });
    finishConfirm(isRail && railwayManualRef.current ? { ...manual, sourceType: "manual", sourcePayload: null } : manual);
  };
  const queryButton = (
    <Button
      type={supportsDistanceQuery && (driving || walking || displayedError) ? "secondary" : "primary"}
      className="itinerary-transport-query-action"
      icon={<ModeIcon size={16} />}
      onClick={() => {
        onDraftStateChange?.(true);
        void (isBus ? queryDriving() : isWalk ? queryWalking() : isRail ? queryRailway() : queryFlight());
      }}
      disabled={
        loading
        || Boolean(routeWarning)
        || (supportsTimetableQuery && !date)
        || !exactEndpointsValid
        || pastTravelDate
        || invalidTimeWindow
      }
    >
      {isBus
        ? (driving ? "重新测算" : "开始大巴测算")
        : isWalk
          ? (walking ? "重新步行测算" : "开始步行测算")
          : hasQueried ? "重新查询" : isRail ? "查询火车方案" : "查询航班方案"}
    </Button>
  );
  const changeProfessional = (segmentId: string) => {
    if (segmentId === segment.id) return;
    if (draftDirtyRef.current) {
      setPendingProfessionalSegmentId(segmentId);
    } else {
      onProfessionalChange?.(segmentId);
    }
  };
  const footer = (
        <div className="itinerary-transport-footer">
          {supportsTimetableQuery ? (
            <div className="itinerary-transport-selection-summary" role="status">
              {compareRailway ? isTransfer
                ? transferRuntime.selected ? `已选定 ${transferRuntime.selected.label} · 比较 ${transferRuntime.selections.length} 个方案` : "选择两段票价，组成一个完整中转方案"
                : railwayFares.length ? `已选定 ${railwayFares[0].trainCode} · 比较 ${railwayFares.length} 个方案` : "请选择票价加入候选比较"
                : isTransfer ? transferRuntime.selected ? `主选 ${transferRuntime.selected.label}${transferRuntime.selections.length > 1 ? `，另有 ${transferRuntime.selections.length - 1} 条备选` : ""}` : "请选择完整中转方案，第一条为主选，其余为备选"
                : isRail && railwayFares.length
                ? `主交通 ${railwayFares[0].trainCode} + 备用 ${railwayFares.length - 1} 条`
                : selectedKey
                ? `主交通 ${selectedOption?.routeCode ?? "-"} + 备用 ${backupKeys.length} 条`
                : hasPersistedTransportFacts(activeSegment)
                ? `已保存方案 ${persistedRouteCode}`
                : "请选择 1 条主交通，可添加多条备用"}
            </div>
          ) : null}
          <div className="itinerary-drawer-actions">
            <Button onClick={cancelDrawer}>{compareRailway ? "关闭并保留" : "取消"}</Button>
            <Button
              type="primary"
              disabled={confirmDisabled || (embeddedRailway && !isTransfer && !railwayFares.length) || Boolean(routeWarning) || (isTransfer && (!transferRuntime.selected || transferRuntime.loading
                || Boolean(railwayTransferUnavailableReason(transferRuntime.selected))))}
              onClick={saveRoute}
            >
              {embeddedRailway ? "添加火车" : "保存路线"}
            </Button>
          </div>
        </div>
  );
  const content = (<>
      {!embeddedRailway && <section className="itinerary-transport-workspace-head" aria-label="交通路线">
        {professionalOptions?.length ? <label className="itinerary-professional-primary">所属专业
          <select ref={professionalSelectRef} aria-label="交通所属专业" value={segment?.id ?? ""}
            onChange={(event) => changeProfessional(event.target.value)}>
            {professionalOptions.map((option) => <option key={option.segmentId} value={option.segmentId}>{option.name}</option>)}
          </select>
        </label> : null}
        <div className="itinerary-transport-mode-switch" role="group" aria-label="交通类型">
          {transportChoices.map((choice) => {
            const ChoiceIcon = choice.icon;
            const activeChoice = activeSegment.transportMode;
            return (
              <button
                key={choice.mode}
                type="button"
                aria-pressed={activeChoice === choice.mode}
                className={activeChoice === choice.mode ? "is-active" : ""}
                onClick={() => changeTransportChoice(choice.mode)}
              >
                <ChoiceIcon size={16} aria-hidden="true" />
                <span>{choice.label}</span>
              </button>
            );
          })}
        </div>
        {!isRail && <div className="itinerary-transport-route-flow" role="group" aria-label={`${queryModeLabel}路线`}>
          <TransportEndpointPicker
            visible={visible}
            label="起点"
            node={selectedFromNode}
            otherNode={selectedToNode}
            transportMode={activeSegment.transportMode}
            listCandidates={listPointCandidates}
            onDraftStateChange={onDraftStateChange}
            onChange={(node) => changeEndpoint("origin", node)}
          />
          <div className="itinerary-transport-route-connector" aria-hidden="true">
            <span />
            <ModeIcon size={18} />
            <ArrowRight size={16} />
            <span />
          </div>
          <TransportEndpointPicker
            visible={visible}
            label="终点"
            node={selectedToNode}
            otherNode={selectedFromNode}
            transportMode={activeSegment.transportMode}
            listCandidates={listPointCandidates}
            onDraftStateChange={onDraftStateChange}
            onChange={(node) => changeEndpoint("destination", node)}
          />
        </div>}
      </section>}
      {supportsQuery && !isRail ? (
        <section
          className={`itinerary-transport-query${supportsDistanceQuery ? " is-distance" : ""}${isBus ? " is-bus" : ""}`}
          aria-label={`${queryModeLabel}${supportsDistanceQuery ? "路线测算" : "方案查询"}条件`}
        >
        <div className={`itinerary-transport-query-controls${supportsDistanceQuery ? " is-distance" : ""}${isBus ? " is-bus" : ""}`}>
          {supportsTimetableQuery ? <label className="itinerary-transport-field is-date">
            <span><CalendarDays size={15} aria-hidden="true" /> 出发日期</span>
            <DatePicker
              allowClear={false}
              className="itinerary-transport-date-picker"
              format="YYYY-MM-DD"
              value={date || undefined}
              status={pastTravelDate ? "error" : undefined}
              disabledDate={(current) => {
                const value = current.format("YYYY-MM-DD");
                return value < today || value > maxQueryDate;
              }}
              inputProps={{
                id: "itinerary-transport-date",
                "aria-label": "出发日期",
                "aria-invalid": pastTravelDate || undefined,
                "aria-describedby": pastTravelDate ? validationErrorId : undefined,
                required: true,
                onBlur: (event) => {
                  const nextDate = event.currentTarget.value.trim();
                  if (/^\d{4}-\d{2}-\d{2}$/.test(nextDate)) changeDate(nextDate);
                },
              }}
              onChange={(nextDate) => changeDate(String(nextDate || ""))}
            />
          </label> : null}
          {supportsTimetableQuery ? (
            <div className="itinerary-transport-time-window" role="group" aria-label="出发时间范围">
              <label className="itinerary-transport-field" htmlFor="itinerary-transport-earliest">
                <span><Clock3 size={15} aria-hidden="true" /> 最早出发</span>
                <input
                  id="itinerary-transport-earliest"
                  type="time"
                  value={earliestDeparture}
                  aria-invalid={invalidTimeWindow || undefined}
                  aria-describedby={invalidTimeWindow ? validationErrorId : undefined}
                  onChange={(event) => {
                    clearResults();
                    setEarliestDeparture(event.target.value);
                    onDraftStateChange?.(true);
                  }}
                />
              </label>
              <ArrowRight className="itinerary-transport-time-arrow" size={15} aria-hidden="true" />
              <label className="itinerary-transport-field" htmlFor="itinerary-transport-latest">
                <span>最晚出发</span>
                <input
                  id="itinerary-transport-latest"
                  type="time"
                  value={latestDeparture}
                  aria-invalid={invalidTimeWindow || undefined}
                  aria-describedby={invalidTimeWindow ? validationErrorId : undefined}
                  onChange={(event) => {
                    clearResults();
                    setLatestDeparture(event.target.value);
                    onDraftStateChange?.(true);
                  }}
                />
              </label>
            </div>
          ) : null}
          {!isBus || !displayedError ? queryButton : null}
        </div>
        </section>
      ) : null}
      {isRail && <Radio.Group type="button" value={railwayMode} aria-label="火车查询方式" style={{ marginBottom: 12 }}
        onChange={(mode: "direct" | "transfer") => {
          clearResults(true); setRailwayMode(mode);
          if (railwayTransferContext) {
            const originNode = mode === "transfer" ? railwayTransferContext.fromNode : fromNode;
            const destinationNode = mode === "transfer" ? railwayTransferContext.toNode : toNode;
            setOriginNodeDraft(originNode); setDestinationNodeDraft(destinationNode);
            setOrigin(originNode?.title ?? ""); setDestination(destinationNode?.title ?? "");
          }
          pendingDateQueryRef.current = true; setRailwayQueryRevision((value) => value + 1);
        }}
        options={[{ label: "直达", value: "direct" }, { label: "中转", value: "transfer" }]} />}
      {isTransfer && railwayTransferContext?.replaceError && <Alert type="warning" content={railwayTransferContext.replaceError} style={{ marginBottom: 12 }} />}
      {isTransfer && <ItineraryRailwayTransferPanel runtime={transferRuntime} date={date} minDate={today} maxDate={maxQueryDate}
        selectionMode={railwaySelectionMode}
        fromName={selectedFromNode?.title ?? origin} toName={selectedToNode?.title ?? destination}
        queryDisabled={transferRuntime.loading || Boolean(routeWarning) || !exactEndpointsValid || !date || pastTravelDate}
        onDateChange={changeDate} onDraftChange={() => onDraftStateChange(true)}
        onSelectDate={(next) => { pendingDateQueryRef.current = true; changeDate(next); setRailwayQueryRevision((value) => value + 1); }}
        origin={<TransportEndpointPicker visible={visible} label="起点" node={selectedFromNode} otherNode={selectedToNode} transportMode="rail" listCandidates={listPointCandidates} onDraftStateChange={onDraftStateChange} onChange={(node) => changeEndpoint("origin", node)} />}
        destination={<TransportEndpointPicker visible={visible} label="终点" node={selectedToNode} otherNode={selectedFromNode} transportMode="rail" listCandidates={listPointCandidates} onDraftStateChange={onDraftStateChange} onChange={(node) => changeEndpoint("destination", node)} />}
      />}
      {isRail && !isTransfer && <ItineraryRailwayQueryPanel
        selectionNote={railwaySelectionNote}
        selectionMode={railwaySelectionMode}
        key={`${segmentKey}|${visible}`}
        options={options} result={railwayMeta} date={date} minDate={today} maxDate={maxQueryDate} purpose={railwayPurpose}
        fromName={selectedFromNode?.title ?? origin} toName={selectedToNode?.title ?? destination}
        loading={loading} error={displayedError} fares={railwayFares}
        onFaresChange={(fares) => {
          railwayManualRef.current = fares.length === 0;
          if (!fares.length) {
            setDraftSegment({ ...routeDraft, sourceType: "manual", sourcePayload: null });
            rememberRailwayEndpoints();
          }
          setRailwayFares(fares); setSelectedKey(""); onDraftStateChange(true);
        }}
        onDateChange={changeDate}
        onPurposeChange={(purpose) => { clearResults(); setRailwayPurpose(purpose); onDraftStateChange(true); }}
        onSelectDate={(next) => { pendingDateQueryRef.current = true; changeDate(next); setRailwayQueryRevision((value) => value + 1); }}
        onQuery={() => { onDraftStateChange(true); void queryRailway(); }}
        queryDisabled={loading || Boolean(routeWarning) || !exactEndpointsValid || !date || pastTravelDate}
        origin={<TransportEndpointPicker visible={visible} label="起点" node={selectedFromNode} otherNode={selectedToNode} transportMode="rail" listCandidates={listPointCandidates} onDraftStateChange={onDraftStateChange} onChange={(node) => changeEndpoint("origin", node)} />}
        destination={<TransportEndpointPicker visible={visible} label="终点" node={selectedToNode} otherNode={selectedFromNode} transportMode="rail" listCandidates={listPointCandidates} onDraftStateChange={onDraftStateChange} onChange={(node) => changeEndpoint("destination", node)} />}
      />}
      {!embeddedRailway && !isTransfer && <section className="itinerary-transport-manual" aria-label="路线信息">
        <header>
          <Route size={16} aria-hidden="true" />
          <strong>路线信息</strong>
          {isRail && <span className="itinerary-railway-manual-note">可直接手动填写；查询失败不影响保存路线</span>}
        </header>
        <ItineraryRouteFields
          draft={routeDraft}
          fromNode={selectedFromNode}
          toNode={selectedToNode}
          departureDayDate={routeDepartureDayDate}
          arrivalDayDate={routeArrivalDayDate}
          estimatedDurationMinutes={isBus
            ? driving?.businessDurationMinutes
            : isWalk
              ? walking?.businessDurationMinutes
              : null}
          durationEditable={supportsDistanceQuery}
          showConstraintWarning={false}
          showTransportTypeFields={false}
          onChange={updateDisplayedRouteDraft}
        />
      </section>}
      {supportsQuery ? (
        <section className="itinerary-transport-query-status" role="region" aria-label="查询状态">
          {routeWarning ? (
            <Alert
              className="itinerary-route-constraint-alert"
              type="warning"
              showIcon
              content={routeWarning}
            />
          ) : null}
          {isTransfer && displayedError ? <Alert type="error" content={displayedError} /> : null}
          {loading && !isRail ? <div className="itinerary-panel-loading"><Spin size={18} /> 正在查询</div> : null}
          {displayedError && !isRail ? (
            <div id={localValidationError ? validationErrorId : undefined} className="itinerary-transport-error">
              <Alert type="error" content={displayedError} />
              {error && !localValidationError ? (
                <Button type={supportsDistanceQuery ? "primary" : "secondary"} icon={<RotateCw size={15} />} onClick={() => void (isBus ? queryDriving() : isWalk ? queryWalking() : isRail ? queryRailway() : queryFlight())}>重试</Button>
              ) : null}
            </div>
          ) : null}
          {railwayMeta?.isReference ? (
            <Alert type="warning" content={railwayReferenceNotice(railwayMeta)} />
          ) : null}
          {dateNotice ? <Alert type="warning" content={dateNotice} /> : null}
          {localTransportNotice && supportsDistanceQuery && !loading && !error ? (
            <Alert type="warning" showIcon content={localTransportNotice} />
          ) : null}
          {isRail && (railwayTimeWindowFallback || railwaySavedOptionsFallback) && !loading && !error && resultKey === currentQueryKey ? (
            <Alert type="warning" content={railwaySavedOptionsFallback
              ? railwaySavedOptionsFallbackMessage(
                  selectedFromNode?.title ?? origin,
                  selectedToNode?.title ?? destination,
                  date,
                  earliestDeparture,
                  latestDeparture,
                  options.length,
                )
              : railwayNoResultsMessage(
                  selectedFromNode?.title ?? origin,
                  selectedToNode?.title ?? destination,
                  date,
                  earliestDeparture,
                  latestDeparture,
                  true,
                  options.length,
                )} />
          ) : null}
          {flightMeta?.isReference ? (
            <Alert type="warning" content={flightMeta.referenceReason === "query_failed"
              ? `原航班查询日期 ${flightMeta.requestedDate ?? date} 未返回可用航班，已自动改查明天 ${flightMeta.referenceDate ?? flightMeta.queryDate ?? date} 的航班，仅供参考。`
              : `航班查询日期 ${flightMeta.requestedDate ?? date} 超出今天后 2 个月范围，已改用 ${flightMeta.referenceDate ?? flightMeta.queryDate ?? date} 查询，仅供参考。`} />
          ) : null}
          {supportsTimetableQuery && !isRail && resultKey === currentQueryKey ? (
            <div className="itinerary-transport-results-toolbar">
              <p>共 {options.length} 个方案</p>
              {options.length > 0 ? (
                <label className="itinerary-inline-select">
                  <span>出发时间</span>
                  <select aria-label="按出发时间排序" value={departureSort}
                    onChange={(event) => setDepartureSort(event.target.value as "asc" | "desc")}>
                    <option value="asc">升序（从早到晚）</option>
                    <option value="desc">倒序（从晚到早）</option>
                  </select>
                </label>
              ) : null}
            </div>
          ) : null}
          {!loading && !error && supportsTimetableQuery && hasQueried && resultKey === currentQueryKey && options.length === 0
            ? isRail
              ? <Alert type="warning" content={railwayNoResultsMessage(
                  selectedFromNode?.title ?? origin,
                  selectedToNode?.title ?? destination,
                  date,
                  earliestDeparture,
                  latestDeparture,
                  Boolean(railwayMeta?.options.length),
                )} />
              : <p className="itinerary-panel-empty">暂无可用方案</p>
            : null}
        </section>
      ) : null}
      {supportsTimetableQuery && !isRail ? <fieldset className="itinerary-transport-options">
        <legend className="sr-only">交通方案选择：选择一条主交通，可添加多条备用</legend>
        {sortedOptions.map((option) => {
          const optionKey = transportSelectionKey(option);
          const seat = selectedSeat(option, selectedSeatClasses);
          const fromName = option.fromName || origin;
          const toName = option.toName || destination;
          const seatLabel = option.provider === "py12306" ? "席别" : "舱位";
          const isPrimary = selectedKey === optionKey;
          const isBackup = backupKeys.includes(optionKey);
          const canSelect = canSelectTransportOption(option);
          const lowest = lowestPrice(option);
          const selectedPrice = seat?.price ?? option.price;
          const selectPrimary = () => {
            if (!canSelect) return;
            setSelectedKey(optionKey);
            setBackupKeys((current) => current.filter((key) => key !== optionKey));
            onDraftStateChange?.(true);
          };
          return (
            <article
              key={optionKey}
              className={`itinerary-transport-option${isPrimary ? " is-primary" : isBackup ? " is-backup" : ""}`}
              onClick={(event) => {
                if ((event.target as HTMLElement).closest("input, select, button, label, a")) return;
                selectPrimary();
              }}
            >
              <header className="itinerary-transport-option-head">
                <div>
                  <span>{option.provider === "py12306" ? "铁路数据" : "航班数据"}</span>
                  <strong>{option.routeCode}</strong>
                </div>
                {isPrimary ? <b>主交通</b> : isBackup ? <b>备用</b> : null}
              </header>
              <div className="itinerary-transport-offer">
                <div className="itinerary-transport-route">
                  <div>
                    <span title={fromName}>{fromName}</span>
                    <strong>{time(option.departureAt)}</strong>
                  </div>
                  <ArrowRight size={19} aria-hidden="true" />
                  <div>
                    <span title={toName}>{toName}</span>
                    <strong>{formatTransportTimeRange(option.departureAt, option.arrivalAt).split("-").at(-1) || time(option.arrivalAt)}</strong>
                  </div>
                </div>
                <div
                  className="itinerary-transport-price"
                  aria-label={`${option.routeCode} 当前票价 ${selectedPrice ?? "-"} 元`}
                >
                  <span>当前票价</span>
                  <strong><small>¥</small>{selectedPrice ?? "-"}</strong>
                </div>
              </div>
              <div className="itinerary-transport-facts">
                <span>时长 {option.durationMinutes ?? "-"} 分钟</span>
                <span>最低 {lowest ?? "-"} 元</span>
                <span>所选 {selectedPrice ?? "-"} 元</span>
                {seat ? <span>{seatLabel} {seat.seatClass} · 余票 {seat.availability ?? "未知"}</span> : null}
              </div>
              {option.seats.length ? (
                <label className="itinerary-inline-select">
                  <span>{seatLabel}</span>
                  <select
                    aria-label={`${option.routeCode} ${seatLabel}`}
                    value={seat?.seatClass ?? ""}
                    onChange={(event) => {
                      setSelectedSeatClasses((current) => ({
                        ...current,
                        [optionKey]: event.target.value,
                      }));
                      onDraftStateChange?.(true);
                    }}
                  >
                    {!seat ? <option value="" disabled>暂无可购买{seatLabel}</option> : null}
                    {option.seats.map((item) => (
                      <option key={item.seatClass} value={item.seatClass} disabled={!isPurchasableSeat(item)}>
                        {item.seatClass} {item.availability ?? ""} {item.price ?? option.price ?? "-"} 元
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <div className="itinerary-transport-choice">
                <label>
                  <input type="radio" name="transport-primary" disabled={!canSelect} checked={isPrimary} onChange={selectPrimary} />
                  主线 {option.routeCode}
                </label>
                <label>
                  <input
                    type="checkbox"
                    disabled={isPrimary || !canSelect}
                    checked={isBackup}
                    onChange={(event) => {
                      setBackupKeys((current) =>
                        event.target.checked ? [...new Set([...current, optionKey])] : current.filter((key) => key !== optionKey),
                      );
                      onDraftStateChange?.(true);
                    }}
                  />
                  备用 {option.routeCode}
                </label>
              </div>
            </article>
          );
        })}
      </fieldset> : null}
      {driving || walking ? (
        <section className="itinerary-driving-result" role="region" aria-label={isWalk ? "步行测算结果" : "大巴测算结果"}>
          <div className="itinerary-driving-distance">
            <span><Route size={16} aria-hidden="true" /> {driving?.source === "deepseek" ? "路线距离（模型估算）" : "路线距离"}</span>
            <strong><b>{isWalk ? walking?.distanceKm : formatDistanceKm(driving?.distanceKm, "bus")}</b><small>公里</small></strong>
          </div>
          <div className="itinerary-driving-duration is-business">
            <span><Clock3 size={15} aria-hidden="true" /> {isWalk ? "预计步行时长" : "业务预计时长"}</span>
            <strong>{isWalk ? walking?.businessDurationMinutes : driving?.businessDurationMinutes} 分钟</strong>
            <small>{isWalk ? "高德步行路线参考时间" : "已按大巴行程预留上下客与道路缓冲"}</small>
          </div>
          {!isWalk ? <>
            <div className="itinerary-driving-duration">
              <span><Clock3 size={15} aria-hidden="true" /> {driving?.source === "deepseek" ? "模型估算时长" : "地图原始时长"}</span>
              <strong>{driving?.rawDurationMinutes} 分钟</strong>
              <small>{driving?.source === "deepseek" ? "DeepSeek 估算，仅供参考" : "地图服务返回的驾车参考时间"}</small>
            </div>
            <dl className="itinerary-driving-facts">
              <div>
                <dt><ReceiptText size={15} aria-hidden="true" /> 过路费</dt>
                <dd>{driving?.tollsYuan} 元</dd>
              </div>
              <div>
                <dt>收费里程</dt>
                <dd>{formatDistanceKm(driving?.tollDistanceKm, "bus")} 公里</dd>
              </div>
              <div>
                <dt>主要道路</dt>
                <dd>{driving?.tollRoad || "暂无收费道路信息"}</dd>
              </div>
            </dl>
          </> : null}
        </section>
      ) : null}
      <div className="sr-only" aria-live="polite">{loading ? "正在查询交通方案" : ""}</div>
  </>);
  if (embeddedRailway) return <section className="itinerary-railway-picker" aria-label="查火车">{content}{railwayFooterContainer ? createPortal(footer, railwayFooterContainer) : footer}</section>;
  return (<>
    <Drawer
      {...workspaceDialogAttributes}
      wrapClassName="itinerary-transport-workspace-dialog"
      className={`itinerary-transport-drawer itinerary-route-modal${isBus ? " is-bus" : ""}${isRail ? " is-rail" : ""}`}
      title={<span aria-label="编辑路线"><span aria-hidden="true">交通工作区</span></span>}
      closeIcon={<ItineraryDialogCloseButton label="关闭编辑路线" />}
      visible={visible}
      escToExit={!pendingProfessionalSegmentId}
      maskClosable={!pendingProfessionalSegmentId}
      width={isRail ? "var(--itinerary-rail-workspace-width)" : 760}
      footer={footer}
      onCancel={cancelDrawer}
      afterClose={afterClose}
    >
      {content}
    </Drawer>
    <Modal
      visible={Boolean(pendingProfessionalSegmentId)}
      title="切换专业交通"
      maskStyle={{ zIndex: 1100 }}
      wrapStyle={{ zIndex: 1101 }}
      escToExit
      closeIcon={<ItineraryDialogCloseButton label="继续编辑当前路线" />}
      okText="放弃并切换"
      cancelText="继续编辑"
      okButtonProps={{ status: "danger" }}
      onCancel={() => setPendingProfessionalSegmentId(null)}
      afterClose={() => professionalSelectRef.current?.focus()}
      onOk={() => {
        if (!pendingProfessionalSegmentId) return;
        const targetSegmentId = pendingProfessionalSegmentId;
        setPendingProfessionalSegmentId(null);
        clearResults();
        onDraftStateChange(false);
        onProfessionalChange?.(targetSegmentId);
      }}
    >
      当前路线有未保存的修改。切换到“{professionalOptions?.find((option) => option.segmentId === pendingProfessionalSegmentId)?.name}”将放弃这些修改，是否继续？
    </Modal>
  </>);
}

type TransportEndpointPickerProps = {
  visible: boolean;
  label: "起点" | "终点";
  node: ItineraryNode | null;
  otherNode: ItineraryNode | null;
  transportMode: TransportMode;
  listCandidates?: (query: PointCandidateQuery, signal?: AbortSignal) => Promise<PointCandidateResult>;
  onDraftStateChange?: (dirty: boolean) => void;
  onChange: (node: ItineraryNode) => void;
};

/** 端点选择只替换固定骨架节点的业务身份，不允许把路线改连到任意现有节点。 */
function TransportEndpointPicker({
  visible,
  label,
  node,
  otherNode,
  transportMode,
  listCandidates,
  onDraftStateChange,
  onChange,
}: TransportEndpointPickerProps) {
  const candidateKind = transportEndpointCandidateKind(transportMode, node);
  const searchControllerRef = useRef<AbortController | null>(null);
  const [keyword, setKeyword] = useState("");
  const [candidates, setCandidates] = useState<PointCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [queried, setQueried] = useState(false);
  const [error, setError] = useState("");
  const generationRef = useRef(0);
  const nodeKey = node
    ? [node.id, node.nodeType, node.pointSpuId, node.stayAreaId, node.organizationSiteId, node.title, node.railwayRegionName].join(":")
    : "none";
  const contextRef = useRef("");
  const candidateContextKey = `${nodeKey}|${candidateKind}`;
  contextRef.current = `${visible ? "open" : "closed"}|${candidateContextKey}|${keyword}`;
  const currentCandidate = candidateFromTransportEndpoint(node);
  const currentCandidateValid = currentCandidate
    ? transportMode === "walk"
      || transportMode === "bus"
      || candidateKindFromCandidate(currentCandidate) === candidateKind
    : false;
  const searchingCandidates = loading && !(currentCandidateValid && !keyword.trim());
  const visibleCandidates = currentCandidateValid && currentCandidate
    && (!keyword.trim() || currentCandidate.name.includes(keyword.trim()))
    ? [...new Map([currentCandidate, ...candidates].map((candidate) => [candidateKey(candidate), candidate])).values()]
    : candidates;
  const otherResourceKey = transportEndpointResourceKey(otherNode);

  const search = async (searchKeyword = keyword, showLoading = true) => {
    if (!listCandidates) return;
    searchControllerRef.current?.abort();
    const controller = new AbortController();
    searchControllerRef.current = controller;
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    const requestKey = `open|${candidateContextKey}|${searchKeyword}`;
    setCandidates([]);
    if (showLoading) setLoading(true);
    setQueried(false);
    setError("");
    try {
      const result = await listCandidates({
        kind: candidateKind,
        ...(candidateKind === "railway_station" ? { railwayScope: "station" } : {}),
        keyword: searchKeyword.trim() || undefined,
        page: 1,
        pageSize: 20,
      }, controller.signal);
      if (!controller.signal.aborted && generation === generationRef.current && requestKey === contextRef.current) {
        setCandidates(result.items.filter((candidate) => candidateKindFromCandidate(candidate) === candidateKind));
        setQueried(true);
      }
    } catch (reason) {
      if (!controller.signal.aborted && generation === generationRef.current && requestKey === contextRef.current) {
        setCandidates([]);
        setError(transportEndpointCandidateError(reason));
      }
    } finally {
      // 请求返回前输入框可能被 Arco Select 同步当前选中项，导致 contextRef 变化；
      // 这不应让本次请求的 loading 状态永久悬挂。代次仍用于保护后续请求。
      if (!controller.signal.aborted && generation === generationRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    generationRef.current += 1;
    setKeyword("");
    setCandidates([]);
    setLoading(false);
    setQueried(false);
    setError("");
  }, [candidateKind, nodeKey, visible]);

  useEffect(() => {
    return () => searchControllerRef.current?.abort();
  }, [candidateKind, keyword, listCandidates, nodeKey, visible]);

  useEffect(() => {
    if (!visible || !listCandidates || !keyword.trim()) return;
    const timer = window.setTimeout(() => void search(keyword), 300);
    return () => window.clearTimeout(timer);
  }, [candidateKind, keyword, listCandidates, nodeKey, visible]);

  return (
    <div className={`itinerary-transport-endpoint-card${label === "终点" ? " is-destination" : ""}`}>
      <MapPin size={17} aria-hidden="true" />
      <div>
        <span>{label}</span>
        <Select
          aria-label={`选择${label}`}
          showSearch
          filterOption={false}
          loading={searchingCandidates}
          disabled={!node || !listCandidates}
          value={currentCandidateValid && currentCandidate ? candidateKey(currentCandidate) : undefined}
          inputValue={keyword}
          placeholder={`搜索${transportEndpointCandidateKindLabel(candidateKind)}`}
          notFoundContent={null}
          onVisibleChange={(open) => {
            if (open && !keyword.trim() && !loading && !queried) void search("", false);
          }}
          options={visibleCandidates.map((candidate) => ({
            value: candidateKey(candidate),
            label: candidate.name,
            disabled: candidateKey(candidate) === otherResourceKey,
          }))}
          onInputValueChange={(value) => {
            const nextKeyword = Array.from(value).slice(0, TRANSPORT_ENDPOINT_KEYWORD_MAX_LENGTH).join("");
            if (nextKeyword === keyword) return;
            searchControllerRef.current?.abort();
            generationRef.current += 1;
            setCandidates([]);
            setLoading(false);
            setQueried(false);
            setError("");
            setKeyword(nextKeyword);
            onDraftStateChange?.(true);
          }}
          onChange={(value) => {
            if (!node) return;
            const selected = visibleCandidates.find((candidate) => candidateKey(candidate) === String(value));
            if (!selected || candidateKey(selected) === otherResourceKey) return;
            searchControllerRef.current?.abort();
            generationRef.current += 1;
            setLoading(false);
            setQueried(false);
            setError("");
            setKeyword("");
            onChange(transportEndpointNodeFromCandidate(node, selected, candidateKind));
          }}
        />
        {searchingCandidates ? <small className="itinerary-transport-endpoint-status"><Spin size={13} /> 正在搜索</small> : null}
        {error ? (
          <small className="itinerary-transport-endpoint-status is-error" role="alert">
            {error}
            <Button size="mini" type="text" onClick={() => void search()}>重试</Button>
          </small>
        ) : null}
        {queried && !loading && !error && candidates.length === 0 ? (
          <small className="itinerary-transport-endpoint-status">暂无匹配候选</small>
        ) : null}
      </div>
    </div>
  );
}

/** 按交通方式与固定节点身份收窄端点主数据类型。 */
function transportEndpointCandidateKind(
  transportMode: TransportMode,
  node: ItineraryNode | null,
): PointCandidateQuery["kind"] {
  if (transportMode === "rail") return "railway_station";
  if (transportMode === "air") return "airport";
  if (node?.nodeType === "stay_area" || node?.nodeType === "organization_site") return node.nodeType;
  return "point";
}

/** 返回端点候选类型的界面名称。 */
function transportEndpointCandidateKindLabel(kind: PointCandidateQuery["kind"]): string {
  const labels: Record<PointCandidateQuery["kind"], string> = {
    point: "考察点",
    transport_station: "交通站点",
    railway_station: "火车站",
    airport: "机场",
    stay_area: "住宿区域",
    organization_site: "学校校区",
  };
  return labels[kind];
}

/** 把当前端点身份映射为下拉框中的已选主数据候选。 */
function candidateFromTransportEndpoint(node: ItineraryNode | null): PointCandidate | null {
  if (!node?.title.trim()) return null;
  if (node.pointSpuId) {
    return {
      refType: "point_spu",
      refId: node.pointSpuId,
      name: node.title,
      pointType: node.nodeType === "railway_station" ? "火车站" : node.nodeType === "airport" ? "机场" : undefined,
      headImageUrl: node.imageUrl,
      railwayRegionName: node.railwayRegionName,
    };
  }
  if (node.stayAreaId) return { refType: "stay_area", refId: node.stayAreaId, name: node.title };
  if (node.organizationSiteId != null) {
    return { refType: "organization_site", refId: String(node.organizationSiteId), name: node.title };
  }
  return null;
}

/** 识别后端候选实际代表的业务节点类型。 */
function candidateKindFromCandidate(candidate: PointCandidate): PointCandidateQuery["kind"] {
  if (candidate.refType === "stay_area") return "stay_area";
  if (candidate.refType === "organization_site") return "organization_site";
  if (candidate.pointType === "火车站" || candidate.pointType === "车站") return "railway_station";
  if (candidate.pointType === "机场") return "airport";
  return "point";
}

/** 生成跨资源类型稳定且不冲突的候选键。 */
function candidateKey(candidate: PointCandidate): string {
  return `${candidate.refType}:${candidate.refId}`;
}

/** 返回节点当前绑定的主数据资源键。 */
function transportEndpointResourceKey(node: ItineraryNode | null): string {
  const candidate = candidateFromTransportEndpoint(node);
  return candidate ? candidateKey(candidate) : "";
}

/** 把主数据候选投影到固定 ID、顺序和时段不变的端点草稿。 */
function transportEndpointNodeFromCandidate(
  node: ItineraryNode,
  candidate: PointCandidate,
  candidateKind: PointCandidateQuery["kind"],
): ItineraryNode {
  const sameResource = candidateKey(candidate) === transportEndpointResourceKey(node);
  return {
    ...node,
    nodeType: candidateKind === "transport_station" ? "point" : candidateKind,
    title: candidate.name.trim(),
    railwayRegionName: candidate.railwayRegionName ?? null,
    description: sameResource ? node.description : null,
    imageUrl: candidate.headImageUrl ?? (sameResource ? node.imageUrl : null),
    pointSpuId: candidate.refType === "point_spu" ? candidate.refId : null,
    stayAreaId: candidate.refType === "stay_area" ? candidate.refId : null,
    organizationSiteId: candidate.refType === "organization_site" ? Number(candidate.refId) : null,
  };
}

/** 比较两个规范化端点草稿是否包含实际业务变化。 */
function sameTransportEndpointDraft(
  left: ReturnType<typeof transportEndpointDraft>,
  right: ReturnType<typeof transportEndpointDraft>,
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

/** 将候选服务错误收敛为可重试的端点内提示。 */
function transportEndpointCandidateError(reason: unknown): string {
  if (isHttpClientError(reason) && (reason.statusCode === null || reason.code === "ERR_NETWORK")) {
    return "候选加载失败，请检查网络";
  }
  return "候选加载失败";
}

/** 从已保存主方案恢复查询站点；旧数据兼容解析标准化 label。 */
type TransportSelectionState = {
  selectedKey: string;
  backupKeys: string[];
  seatClasses: Record<string, string>;
};

/** 首次成功查询时，仅恢复结果中仍可选择的持久化主备方案。 */
function restorePersistedSelections(segment: ItinerarySegment, options: TransportOption[]): TransportSelectionState {
  const available = selectableTransportOptions(options);
  const selected = segment.sourcePayload?.selected;
  const restoredSelected = selected
    ? available.get(transportSelectionKey(selected))
    : [...available.values()].find((option) => matchesLegacySegmentSelection(segment, option));
  const restoredBackups = (segment.sourcePayload?.backups ?? [])
    .filter((backup) => available.has(transportSelectionKey(backup)))
    .filter((backup) => transportSelectionKey(backup) !== (restoredSelected ? transportSelectionKey(restoredSelected) : ""));
  const seatClasses: Record<string, string> = {};
  [selected, ...restoredBackups].forEach((saved) => {
    if (!saved) return;
    const option = available.get(transportSelectionKey(saved));
    const seatClass = saved.seatClass ?? saved.cabinClass;
    if (option && seatClass && option.seats.some((seat) => seat.seatClass === seatClass && isPurchasableSeat(seat))) {
      seatClasses[transportSelectionKey(option)] = seatClass;
    }
  });
  return {
    selectedKey: restoredSelected ? transportSelectionKey(restoredSelected) : "",
    backupKeys: [...new Set(restoredBackups.map(transportSelectionKey))],
    seatClasses,
  };
}

/** 兼容仅保存顶层班次与时刻的历史路线，避免重查后误选返回列表第一项。 */
function matchesLegacySegmentSelection(segment: ItinerarySegment, option: TransportOption) {
  const routeCode = segment.routeCode?.trim().toLowerCase();
  if (!routeCode || option.routeCode?.trim().toLowerCase() !== routeCode) return false;
  const segmentDeparture = segment.departureAt?.slice(11, 16);
  const segmentArrival = segment.arrivalAt?.slice(11, 16);
  return (!segmentDeparture || segmentDeparture === time(option.departureAt))
    && (!segmentArrival || segmentArrival === time(option.arrivalAt));
}

/** 后续查询只裁剪当前抽屉草稿，不再回灌旧持久化值。 */
function reconcileTransportSelections(options: TransportOption[], current: TransportSelectionState): TransportSelectionState {
  const available = selectableTransportOptions(options);
  const selectedKey = available.has(current.selectedKey) ? current.selectedKey : "";
  const backupKeys = current.backupKeys.filter((key) => key !== selectedKey && available.has(key));
  const seatClasses = Object.fromEntries(Object.entries(current.seatClasses).filter(([key, seatClass]) => {
    const option = available.get(key);
    return option?.seats.some((seat) => seat.seatClass === seatClass && isPurchasableSeat(seat));
  }));
  return { selectedKey, backupKeys: [...new Set(backupKeys)], seatClasses };
}

/** 查询返回可用方案但未恢复主选时，默认使用结果顺序中的第一项。 */
function withDefaultPrimarySelection(
  options: TransportOption[],
  current: TransportSelectionState,
): TransportSelectionState {
  if (current.selectedKey) return current;
  const firstKey = selectableTransportOptions(options).keys().next().value ?? "";
  if (!firstKey) return current;
  return {
    ...current,
    selectedKey: firstKey,
    backupKeys: current.backupKeys.filter((key) => key !== firstKey),
  };
}

/** 返回当前结果中仍可作为主备交通的方案。 */
function selectableTransportOptions(options: TransportOption[]) {
  return new Map(options.filter(canSelectTransportOption).map((option) => [transportSelectionKey(option), option]));
}

/** 返回方案可展示的最低价格。 */
function lowestPrice(option: TransportOption) {
  const prices = [option.price, ...option.seats.filter(isPurchasableSeat).map((seat) => seat.price)]
    .filter((price): price is number => price !== null && price !== undefined);
  return prices.length ? Math.min(...prices) : null;
}

/** 从业务节点构造铁路站点自动解析端点，保留可追溯的主数据引用。 */
/** 仅用于未注入解析器的组件级测试；生产页面始终通过后端解析官方站点。 */
/** 将官方铁路站点投影到通用点位候选，供多选框与手动搜索共用。 */
/** 手动搜索时保留已选自动匹配站点，并按主数据 ID 去重。 */
/** 提取本地展示所需的时分。 */
function time(value?: string | null) {
  return value?.slice(11, 16) || "--:--";
}

/** 按可选出发时间窗保留方案，边界时间包含在内。 */
function filterByDepartureTime(options: TransportOption[], earliest: string, latest: string) {
  if (!earliest && !latest) return options;
  return options.filter((option) => {
    const departure = time(option.departureAt);
    if (departure === "--:--") return true;
    return (!earliest || departure >= earliest) && (!latest || departure <= latest);
  });
}

/** 生成与交通查询快照一致的铁路站点/日期键。 */
function railwaySnapshotQueryKey(fromStationIds: string[], toStationIds: string[], date: string) {
  return `${fromStationIds.join(",")}-${toStationIds.join(",")}-${date}`;
}

/** 无实时车次时优先恢复完全匹配的全天快照，否则使用当前路线段保存的同日车次。 */
function railwayFallbackOptions(
  segment: ItinerarySegment,
  queryKey: string,
  travelDate: string,
  fromNodeId: string | undefined,
  toNodeId: string | undefined,
  fromName: string,
  toName: string,
  queryScope: "station" | "region",
): { options: TransportOption[]; source: "snapshot" | "saved-route" } | null {
  const snapshot = segment.sourcePayload?.toolResultSnapshot;
  const savedScope = snapshot?.railwayQueryScope ?? "station";
  if (snapshot?.queryKey === queryKey && snapshot.provider === "py12306" && savedScope === queryScope) {
    const options = snapshot.options.flatMap(railwayOptionFromSelection);
    if (options.length) return { options, source: "snapshot" };
  }

  if (queryScope === "region") return null;
  if (segment.fromNodeId !== fromNodeId || segment.toNodeId !== toNodeId) return null;
  const sourcePayload = segment.sourcePayload;
  const savedSelections = [sourcePayload?.selected, ...(sourcePayload?.backups ?? [])]
    .filter((selection): selection is NonNullable<typeof selection> => Boolean(
      selection
      && selection.provider === "py12306"
      && selection.departureAt?.slice(0, 10) === travelDate
      && (!selection.fromName || selection.fromName.trim() === fromName.trim())
      && (!selection.toName || selection.toName.trim() === toName.trim())
    ))
    .flatMap(railwayOptionFromSelection);
  const legacyRouteCode = segment.routeCode?.trim();
  const legacyDeparture = segment.departureAt;
  const legacyOption = legacyRouteCode && legacyDeparture?.slice(0, 10) === travelDate
    ? [{
        optionId: `saved-itinerary:${segment.id}:${travelDate}:${legacyRouteCode}:${legacyDeparture.slice(11, 16)}`,
        provider: "py12306" as const,
        routeCode: legacyRouteCode,
        fromName,
        toName,
        departureAt: legacyDeparture,
        arrivalAt: segment.arrivalAt ?? null,
        durationMinutes: segment.durationMinutes ?? null,
        price: null,
        seats: [],
      }]
    : [];
  const optionsByTrip = new Map<string, TransportOption>();
  [...savedSelections, ...legacyOption].forEach((option) => {
    const key = `${option.routeCode.toUpperCase()}|${option.departureAt ?? ""}|${option.arrivalAt ?? ""}`;
    if (!optionsByTrip.has(key)) optionsByTrip.set(key, option);
  });
  const options = [...optionsByTrip.values()];
  return options.length ? { options, source: "saved-route" } : null;
}

/** 将持久化铁路方案投影为可继续选择的显示项。 */
function railwayOptionFromSelection(
  selection: NonNullable<NonNullable<ItinerarySegment["sourcePayload"]>["selected"]>,
): TransportOption[] {
  if (selection.provider !== "py12306" || !selection.optionId || !selection.routeCode) return [];
  const parsedPrice = selection.price ? Number(selection.price) : null;
  const price = parsedPrice !== null && Number.isFinite(parsedPrice) ? parsedPrice : null;
  return [{
    optionId: selection.optionId,
    provider: "py12306",
    routeCode: selection.routeCode,
    fromName: selection.fromName ?? null,
    toName: selection.toName ?? null,
    fromStationId: selection.fromStationId,
    toStationId: selection.toStationId,
    departureAt: selection.departureAt ?? null,
    arrivalAt: selection.arrivalAt ?? null,
    durationMinutes: selection.durationMinutes ?? null,
    price,
    seats: selection.seatClass
      ? [{ seatClass: selection.seatClass, availability: null, price }]
      : [],
  }];
}

/** 为被日期窗口自动修正的历史行程给出明确提示。 */
function transportDateNotice(mode: TransportMode | undefined, value: string) {
  if (!value || mode === "bus" || mode === "walk") return "";
  const normalized = normalizeTransportDate(mode, value);
  if (!normalized || normalized === value) return "";
  return mode === "rail"
    ? `原行程日期 ${value} 不在火车可查询范围（今天至今天后 14 天），查询日期已调整为 ${normalized}，点击查询后使用。`
    : `原行程日期 ${value} 不在航班可查询范围（今天起 2 个月），查询日期已调整为 ${normalized}，点击查询后使用。`;
}

/** 区分当天无车次与车次被当前时段筛掉，给出对应的核对或调整建议。 */
function railwayNoResultsMessage(
  from: string,
  to: string,
  date: string,
  earliest: string,
  latest: string,
  hasDailyOptions: boolean,
  displayedDailyOptionCount = 0,
): string {
  const route = `${from.trim() || "起点站"} → ${to.trim() || "终点站"}`;
  const travelDate = date || "所选日期";
  const timeRange = earliest && latest ? `${earliest} 至 ${latest}` : "全天";
  if (hasDailyOptions) {
    return `当天查到 ${route} 在 ${travelDate} 有直达车次，但当前 ${timeRange} 时段内没有车次；已展示当天全部 ${displayedDailyOptionCount} 个车次，请核对发车时间。`;
  }
  return `当天未查到 ${route} 在 ${travelDate} 的直达车次（当前查询时段：${timeRange}）。请核对所选车站；如需查询其他时段，也可调整最早/最晚发车时间后重试。`;
}

/** 上游无实时结果但展示了行程中保存的同日车次时给出单条说明。 */
function railwaySavedOptionsFallbackMessage(
  from: string,
  to: string,
  date: string,
  earliest: string,
  latest: string,
  count: number,
) {
  const route = `${from.trim() || "起点站"} → ${to.trim() || "终点站"}`;
  const travelDate = date || "所选日期";
  const timeRange = earliest && latest ? `${earliest} 至 ${latest}` : "全天";
  return `当天车次查询未返回 ${route} 在 ${travelDate} 当前 ${timeRange} 时段的实时结果，已展示行程中保存的 ${count} 条同日车次信息；请核对发车时刻。`;
}

/** 将交通业务错误码转换为稳定中文，避免暴露 HTTP 或 provider 原始信息。 */
function transportErrorMessage(reason: unknown, emptyRailwayMessage?: string): string {
  if (isHttpClientError(reason)) {
    const messages: Record<string, string> = {
      ITINERARY_TRANSPORT_CONFIG_MISSING: "交通查询服务未配置，请联系管理员。",
      ITINERARY_TRANSPORT_CLI_MISSING: "航班查询程序未配置。请在后端配置 FLYAI_CLI_PATH 后重启服务。",
      ITINERARY_TRANSPORT_TIMEOUT: "交通查询超时，请稍后重试。",
      ITINERARY_TRANSPORT_EMPTY: emptyRailwayMessage ?? "暂无可用交通方案，请调整条件后重试。",
      ITINERARY_TRANSPORT_INVALID: "交通查询条件无效，请检查后重试。",
      ITINERARY_TRANSPORT_WALKING_OUT_OF_RANGE: "步行路线超出地图测算范围，请调整起终点后重试。",
      ITINERARY_REFERENCE_INVALID: "交通查询端点无效，请检查后重试。",
    };
    if (reason.code && messages[reason.code]) return messages[reason.code];
    if (reason.code === "ECONNABORTED" || reason.code === "ETIMEDOUT") {
      return "交通查询超时，请稍后重试。";
    }
    if (reason.code?.startsWith("ITINERARY_TRANSPORT_")) return "交通查询失败，请稍后重试。";
    if (reason.statusCode === 400 || reason.statusCode === 422) return "交通查询条件无效，请检查后重试。";
    if (reason.statusCode === null || reason.code === "ERR_NETWORK") return "交通查询失败，请检查网络后重试。";
    return "交通查询失败，请稍后重试。";
  }
  return "交通查询失败，请稍后重试。";
}

/** 航班自动查询只接受明确的机场名称，避免把火车站或普通点位误发给航班服务。 */
